import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PdfMarkingWorkspace } from "./pdf-marking-workspace";
import { generateFromStudyDocument } from "./generate-from-marks";
import {
  getStudyDocument,
  saveStudyMarks,
  serverMarkToLocal,
  localMarkToServer,
  type StudyDocumentRead,
} from "@/api/study";
import {
  loadPdf,
  savePdfAs,
  saveMarks,
  loadMarks,
  saveUnderstanding,
  type DocUnderstanding,
} from "@/create/lecture/lecture-pdf-store";
import type { MarkingUserResponse } from "@/api/lecture";
import { Alert, Spinner } from "@/components/ui";

// Frameworks the marks can be generated into (the round system's "generate
// later" target). Limited to the frameworks with a discussion profile, since
// the marked pipeline seeds that framework's teacher discussion.
const TARGET_FRAMEWORKS: { slug: string; label: string }[] = [
  { slug: "learn-a-topic", label: "Learn a Topic" },
  { slug: "deep-dive", label: "Deep Dive" },
  { slug: "math-deep-dive", label: "Math Deep Dive" },
  { slug: "lecture-exercise-recap", label: "Lecture · Exercise · Recap" },
  { slug: "master-a-trait", label: "Master a Trait" },
];

// Server-backed Study host: `:pdfRef` is now a server docId. Loads the document,
// ensures the PDF blob + marks + understanding are seeded into IndexedDB (the
// marking workspace's local render + mark store), then mounts the shared
// <PdfMarkingWorkspace>. Marks autosave back to the server; generation reuses the
// already-stored source PDF (no re-upload / re-extract).
export function StudyPage() {
  const { t } = useTranslation();
  const { pdfRef: docId = "" } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<StudyDocumentRead | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [targetSlug, setTargetSlug] = useState(TARGET_FRAMEWORKS[0].slug);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  // ── Load the document + seed IndexedDB (blob, marks, understanding) ─────────
  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    setReady(false);
    setLoadError(null);
    (async () => {
      try {
        const d = await getStudyDocument(docId);
        if (cancelled) return;
        setDoc(d);

        // 1) Ensure the PDF blob is cached locally under the docId key.
        const cached = await loadPdf(docId);
        if (!cached) {
          if (!d.pdf_url) throw new Error(t("Couldn't reach file storage."));
          const res = await fetch(d.pdf_url);
          if (!res.ok) throw new Error(t("Couldn't reach file storage."));
          const blob = await res.blob();
          await savePdfAs(docId, blob);
        }

        // 2) Seed the local mark store from the server (source of truth).
        const marks = d.marks ?? { deep: [], lookups: [], highlights: [] };
        await saveMarks(docId, {
          deep: (marks.deep ?? []).map(serverMarkToLocal),
          lookups: (marks.lookups ?? []).map(serverMarkToLocal),
          highlights: (marks.highlights ?? []).map(serverMarkToLocal),
        });

        // 3) Seed the one-time understanding cache so the workspace doesn't
        //    recompute it (best-effort — an odd shape just gets recomputed).
        if (d.understanding) {
          try {
            await saveUnderstanding(docId, d.understanding as unknown as DocUnderstanding);
          } catch {
            /* ignore — workspace will recompute understanding */
          }
        }

        if (!cancelled) setReady(true);
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof TypeError
            ? t("Couldn't reach file storage.")
            : e instanceof Error
              ? e.message
              : t("Could not load this document.");
        setLoadError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId, t]);

  // ── Debounced mark mirror: read IndexedDB → PATCH the server ────────────────
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMarksChange = useCallback(() => {
    if (!docId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void loadMarks<{
        deep?: Record<string, unknown>[];
        lookups?: Record<string, unknown>[];
        highlights?: Record<string, unknown>[];
      }>(docId).then((s) => {
        if (!s) return;
        void saveStudyMarks(docId, {
          deep: (s.deep ?? []).map(localMarkToServer),
          lookups: (s.lookups ?? []).map(localMarkToServer),
          highlights: (s.highlights ?? []).map(localMarkToServer),
        }).catch(() => {});
      });
    }, 600);
  }, [docId]);

  const generate = useCallback(
    async (payload: MarkingUserResponse) => {
      if (!doc) return;
      setGenError(null);
      setGenerating(true);
      try {
        const runId = await generateFromStudyDocument({
          storageKey: doc.storage_key,
          sourceText: doc.extracted_text ?? "",
          payload,
          slug: targetSlug,
          title: doc.filename,
        });
        navigate(`/app/create/run/${runId}`);
      } catch (e) {
        setGenError(e instanceof Error ? e.message : t("Couldn't start generation."));
        setGenerating(false);
      }
    },
    [doc, navigate, targetSlug, t],
  );

  return (
    <div className="animate-fade-up">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/app/study" className="text-sm text-brand-600 hover:underline">
          ← {t("Study")}
        </Link>
        <h1 className="truncate text-xl font-bold tracking-tight">
          {doc?.filename ?? t("Mark & study")}
        </h1>
        <label className="ml-auto flex items-center gap-2 text-xs text-ink-faint">
          {t("Build as")}
          <select
            value={targetSlug}
            onChange={(e) => setTargetSlug(e.target.value)}
            disabled={generating}
            className="rounded-lg border border-black/10 bg-white px-2 py-1 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            {TARGET_FRAMEWORKS.map((f) => (
              <option key={f.slug} value={f.slug}>
                {t(f.label)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {genError && (
        <div className="mb-4">
          <Alert>{genError}</Alert>
        </div>
      )}

      {loadError ? (
        <Alert>{loadError}</Alert>
      ) : !ready ? (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      ) : (
        <PdfMarkingWorkspace
          pdfRef={docId}
          documentRef={docId}
          language="en"
          onMarksChange={onMarksChange}
          footerNote={t("Saved to your library — reopen any time, on any device.")}
          primaryAction={{
            label: t("Generate a study set from these marks"),
            loading: generating,
            onClick: (payload) => void generate(payload),
          }}
        />
      )}
    </div>
  );
}
